import React from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { buildBreadcrumb } from "../lib/constants/routeLabels";
import { useBreadcrumbLabelValue } from "../stores/breadcrumbStore";
import { AppLink } from "./AppLink";

interface AppBreadcrumbProps {
  /** Navigation handler — the shell owns the view-transition-aware navigate. */
  onNavigate: (path: string) => void;
  className?: string;
}

/**
 * Topbar breadcrumb — the page's position in the hierarchy, in the space the
 * search input used to occupy.
 *
 * Dar ekranlarda yalnızca son iki halka gösterilir: topbar tek satır yüksekliğinde
 * ve breadcrumb burada ikincil bir yön göstergesi, birincil gezinme değil.
 */
export const AppBreadcrumb: React.FC<AppBreadcrumbProps> = ({
  onNavigate,
  className,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dynamicLabel = useBreadcrumbLabelValue();
  const entries = buildBreadcrumb(pathname);

  // Dinamik bir kayda açılan sayfa kendi adını bildirmişse son halkayı o alır;
  // route tablosu o adı bilemez.
  const last = entries[entries.length - 1];
  if (dynamicLabel && last) {
    entries[entries.length - 1] = { ...last, labelKey: undefined, rawLabel: dynamicLabel };
  }

  if (entries.length === 0) return null;

  return (
    <nav
      aria-label={t("topbar.breadcrumb.aria")}
      className={cn("min-w-0 flex-1", className)}
    >
      <ol className="flex items-center gap-1 overflow-hidden">
        {entries.map((entry, index) => {
          const isLast = index === entries.length - 1;
          const label = entry.rawLabel ?? (entry.labelKey ? t(entry.labelKey) : "");
          // Dar ekranda yalnız son iki halka; ilkler gizlenir.
          const hideOnNarrow = index < entries.length - 2;

          return (
            <li
              key={`${entry.path ?? "leaf"}-${index}`}
              className={cn(
                "flex min-w-0 items-center gap-1",
                hideOnNarrow && "max-md:hidden",
              )}
            >
              {index > 0 && (
                <ChevronRight
                  size={14}
                  className={cn(
                    "shrink-0 text-muted-foreground/50",
                    hideOnNarrow && "max-md:hidden",
                  )}
                  aria-hidden="true"
                />
              )}
              {isLast || !entry.path ? (
                <span
                  className="truncate text-sm font-medium text-foreground"
                  aria-current={isLast ? "page" : undefined}
                >
                  {label}
                </span>
              ) : (
                <AppLink
                  to={entry.path}
                  onNavigate={onNavigate}
                  className="truncate rounded text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {label}
                </AppLink>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default AppBreadcrumb;
